import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import getLocalePropertyForItem from '../services/getLocalePropertyForItem';
import loadTranslationDataAndStatus from '../services/loadTranslationDataAndStatus';
import checkItemUnderSizeLimit from '../utils/checkItemUnderSizeLimit';
import shouldTranslateAutomatic from 'owa-translation/lib/utils/shouldTranslateAutomatic';
import StoredTranslationData from '../store/schema/StoredTranslationData';
import translateItem from './translateItem';
import { action } from 'satcheljs/lib/legacy';
import { ClientItem, mailStore } from 'owa-mail-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    lazyCheckCanTranslate,
    lazyInitSupportedLanguages,
    lazyGetOgmaSupportedLanguageTag,
    lazyGetLanguageNameFromTag,
    lazyGetTargetLanguageName,
    lazyGetTargetLanguageTag,
    lazyGetIsUserLocaleSupported,
} from 'owa-translation';
import getQuotedTextLocale from '../utils/getQuotedTextLocale';
import isCachedContentValid from '../utils/isCachedContentValid';

export default action('setItemLocale')(async function setItemLocale(
    isConversationItemPart: boolean,
    item: ClientItem,
    subject: string
) {
    const initSupportedLanguages = await lazyInitSupportedLanguages.import();
    const getOgmaSupportedLanguageTag = await lazyGetOgmaSupportedLanguageTag.import();
    const getLanguageNameFromTag = await lazyGetLanguageNameFromTag.import();
    const getTargetLanguageName = await lazyGetTargetLanguageName.import();
    const getTargetLanguageTag = await lazyGetTargetLanguageTag.import();
    const getIsUserLocaleSupported = await lazyGetIsUserLocaleSupported.import();
    const checkCanTranslate = await lazyCheckCanTranslate.import();

    // Is this function executing first time for this item.
    const firstRun = !item.TranslationData.userLanguage;

    let locale = item.TranslationData.itemLocale;
    if (locale == null) {
        const allLocales = await getLocalePropertyForItem(item);
        locale = allLocales[0];
        item.TranslationData.quotedTextLocales =
            allLocales[1] != null ? JSON.parse(allLocales[1]) : [];
    }
    const ogmaLocale = getOgmaSupportedLanguageTag(locale);

    // Ensure Ogma data is loaded. Can't call any other Ogma function before this call.
    const userChangedLanguage = await initSupportedLanguages();
    // Check if user locale has changed since last time.
    const localeChanged =
        !firstRun &&
        (userChangedLanguage || getTargetLanguageName() != item.TranslationData.userLanguage);

    let conversationItemParts: ConversationItemParts;
    if (firstRun || localeChanged) {
        if (isConversationItemPart) {
            conversationItemParts = mailStore.conversations.get(item.ConversationId.Id);
        }
        // If user locale is not supported or size is too big, return
        if (
            !getIsUserLocaleSupported() ||
            !checkItemUnderSizeLimit(item.ItemId.Id, isConversationItemPart)
        ) {
            // but, update isTranslatable before leaving
            item.TranslationData.isTranslatable = false;
            item.TranslationData.isShowingTranslation = false;
            item.TranslationData.translationText = null;
            item.TranslationData.isForwardContentTranslatable = false;
            item.TranslationData.isShowingForwardContentTranslation = false;
            item.TranslationData.forwardContentTranslationText = null;

            if (isConversationItemPart) {
                conversationItemParts.subjectTranslationData.isShowingTranslation = false;
                conversationItemParts.subjectTranslationData.translationText = null;
            } else {
                item.TranslationData.isShowingSubjectTranslation = false;
                item.TranslationData.subjectTranslationText = null;
            }

            return;
        }
        item.TranslationData.userLanguage = getTargetLanguageName();
        item.TranslationData.itemOgmaLocale = ogmaLocale;
        item.TranslationData.itemLocaleName = getLanguageNameFromTag(ogmaLocale);
        const isTranslatable = checkCanTranslate(getTargetLanguageTag(), locale);

        if (isFeatureEnabled('rp-inlineTranslationForwardContent2') && isConversationItemPart) {
            let quotedTextOgmaLocales: string[] = [];
            item.TranslationData.quotedTextLocales.forEach(qtLocale => {
                quotedTextOgmaLocales.push(getOgmaSupportedLanguageTag(qtLocale));
            });
            // Get forward content locale
            const forwardContentLocale = getQuotedTextLocale(
                item,
                quotedTextOgmaLocales,
                getTargetLanguageTag(),
                checkCanTranslate
            );

            if (forwardContentLocale) {
                item.TranslationData.forwardContentLocale = forwardContentLocale;
                item.TranslationData.forwardContentLocaleName = getLanguageNameFromTag(
                    forwardContentLocale
                );
                item.TranslationData.isForwardContentTranslatable = true;

                if (!ogmaLocale) {
                    // The main body email is empty and we need to set the locale from the forwarded content.
                    item.TranslationData.itemOgmaLocale = forwardContentLocale;
                    item.TranslationData.itemLocaleName =
                        item.TranslationData.forwardContentLocaleName;
                }
            } else if (forwardContentLocale === '') {
                // There is forward content but we can't determine if it can be translated or not.
                item.TranslationData.isForwardContentTranslatable = isTranslatable;
            } else {
                // There is no forward content or the content is not translatable.
                item.TranslationData.isForwardContentTranslatable = false;
                item.TranslationData.forwardContentTranslationText = null;
            }
        } else if (
            !isFeatureEnabled('rp-inlineTranslationForwardContent2') &&
            isConversationItemPart
        ) {
            // This will be executed until the flight rp-inlineTranslationForwardContent2 hits production.
            // We will translate forward content using the First Body locale detected properties.
            // To avoid checking whether or not there is forward content, will check that out only if we click translate.
            // It is in translateItem.ts where we check for the actual forward content.
            item.TranslationData.isForwardContentTranslatable = isTranslatable;
        } else if (
            isFeatureEnabled('rp-inlineTranslation-conversationViewOff') &&
            !isConversationItemPart
        ) {
            // We will translate the Quoted Text along with the First Body when the First Body is translatable.
            item.TranslationData.isForwardContentTranslatable = isTranslatable;
        } else {
            item.TranslationData.isForwardContentTranslatable = false;
        }

        if (item.TranslationData.itemLocale == null) {
            item.TranslationData.itemLocale = locale;
        }

        let allowAutoTranslation = true;
        if (firstRun) {
            const dataAndStatus = isFeatureEnabled('tra-cacheTranslation')
                ? await loadTranslationDataAndStatus(item)
                : [null, null];
            let isShowingTranslation = dataAndStatus[1] === 'true';
            if (dataAndStatus[1] !== null) {
                allowAutoTranslation = false;
            }
            const transData = JSON.parse(dataAndStatus[0]) as StoredTranslationData;
            const userLanguageTag = getTargetLanguageTag();

            let isCachedTranslationValid = true;

            if (isFeatureEnabled('tra-useTranslationClientType') && transData) {
                isCachedTranslationValid = isCachedContentValid(transData);
            }

            if (
                isCachedTranslationValid &&
                transData &&
                transData.language == userLanguageTag &&
                transData.isConversation == isConversationItemPart
            ) {
                if (isConversationItemPart) {
                    if (!conversationItemParts.subjectTranslationData.translationText) {
                        conversationItemParts.subjectTranslationData.translationText =
                            transData.subject;
                    }
                } else {
                    if (!item.TranslationData.subjectTranslationText) {
                        item.TranslationData.subjectTranslationText = transData.subject;
                    }
                }

                item.TranslationData.translationText = transData.body;
                if (transData.forward && transData.forward.length > 0) {
                    item.TranslationData.forwardContentTranslationText = transData.forward;
                } else {
                    item.TranslationData.forwardContentTranslationText = null;
                }
            } else {
                if (isShowingTranslation) {
                    allowAutoTranslation = true;
                    isShowingTranslation = false;
                }
                item.TranslationData.translationText = null;
                item.TranslationData.forwardContentTranslationText = null;
            }

            if (isShowingTranslation) {
                if (item.TranslationData.translationText) {
                    item.TranslationData.isShowingTranslation = true;
                }
                if (isConversationItemPart) {
                    if (conversationItemParts.subjectTranslationData.translationText) {
                        conversationItemParts.subjectTranslationData.isShowingTranslation = true;
                    }
                } else {
                    if (item.TranslationData.subjectTranslationText) {
                        item.TranslationData.isShowingSubjectTranslation = true;
                    }
                }

                if (
                    isFeatureEnabled('rp-inlineTranslationForwardContent2') &&
                    item.TranslationData.forwardContentTranslationText
                ) {
                    item.TranslationData.isShowingForwardContentTranslation = true;
                }
                if (!isTranslatable && !item.TranslationData.isForwardContentTranslatable) {
                    item.TranslationData.manuallyTranslated = true;
                }
            }
        }

        item.TranslationData.isTranslatable = isTranslatable;
        if (localeChanged) {
            item.TranslationData.isShowingTranslation = false;
            item.TranslationData.translationText = null;
            item.TranslationData.isShowingForwardContentTranslation = false;
            item.TranslationData.forwardContentTranslationText = null;

            // if we are reverting the item due a real language change we need to clear the subject translation
            if (isConversationItemPart) {
                conversationItemParts.subjectTranslationData.isShowingTranslation = false;
                conversationItemParts.subjectTranslationData.translationText = null;
            } else {
                item.TranslationData.isShowingSubjectTranslation = false;
                item.TranslationData.subjectTranslationText = null;
            }
        }

        if (isFeatureEnabled('rp-inlineTranslationAutomatic') && allowAutoTranslation) {
            const bodyTranslatable =
                item.TranslationData.isTranslatable &&
                shouldTranslateAutomatic(item.TranslationData.itemOgmaLocale);

            let forwardContentTranslatable = false;
            if (isFeatureEnabled('rp-inlineTranslationForwardContent2')) {
                forwardContentTranslatable =
                    item.TranslationData.isForwardContentTranslatable &&
                    shouldTranslateAutomatic(item.TranslationData.forwardContentLocale);
            }

            if (bodyTranslatable || forwardContentTranslatable) {
                translateItem(item.ItemId.Id, subject, isConversationItemPart, false, true);
            }
        }
    }
});
